import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { argon2Kdf } from '../crypto/argon2-kdf';
import { Ad, rsaWrapKey, sealPersonal } from '../crypto/envelopes';
import { InboxOpener } from '../crypto/inbox-opener';
import {
    Argon2Kdf, ARGON2_STANDARD, BackupLock, BackupV5, defaultRng, encodeV5, isV5, KeyBackupWrongSecret,
    openBody, openLock, parseV5, PIN_PATTERN, sealBody, sealV5
} from '../crypto/key-backup-v5';
import { KeyRing, KeyRingConflict, pqFingerprint, RingAlg, RingKey, umkFingerprint } from '../crypto/key-ring';
import { rsaFingerprint } from '../crypto/rsa-fingerprint';
import { derivePqPublicKeyJson, generatePqKeyPair, randomBytes, unb64 } from '../crypto/wrap';
import { CardDeltaItem, CardSharesResponse, InboxItem, InboxPage, MigrationStatus, RotateCardRequest, RotateRecipient, SettleItem } from '../models/card-v2.model';
import { AccountStatusService } from './account-status.service';
import { AuthService } from './auth.service';
import { CardApiService } from './cardapi.service';
import { CryptoService, ringPqKeyVersion } from './crypto.service';
import { KeyBackupService } from './key-backup.service';
import { readCode } from './payment-required.interceptor';
import { PendingKeyKind, PendingKeyRecord, PendingKeyStore, saveForAccount } from './pending-key-store.service';
import { RsaRemovalStatusService } from './rsa-removal-status.service';

/** Yeni yedegin yeri (karar 2): sunucu, dosya ya da ikisi; en az biri. */
export type RsaMigrationPlace = 'server' | 'download' | 'both';

/** Arayuzun bir sonraki `run` cagrisina verdigi girdiler; hepsi istege bagli. */
export interface RsaMigrationInput {
    /** Yeni PIN (4-6 hane), yalniz `needPin` sonrasi. */
    pin?: string;
    /** Sunucudaki v5 yedegin PIN'i ya da kurtarma kodu (`needBackupSecret`/`wrongSecret` sonrasi). */
    secret?: string;
    place?: RsaMigrationPlace;
    /** Kullanicinin geri sectigi yeni anahtar dosyasi (`needFile`/`fileMismatch` sonrasi). */
    file?: Uint8Array;
}

/**
 * Tasima kosusunun sonucu. Arayuz yalniz bunu metne esler; ham sunucu metni gosterilmez.
 *  - disabled/skipped: sessiz (bayrak kapali, oturumda anahtar yok, hesap degisti).
 *  - needPin/needPlace/needBackupSecret/wrongSecret/needFile/fileMismatch: kullanicidan girdi.
 *  - done: tasima bitti (`fresh` = bu kosuda bitti; false ise sessiz temizlik).
 *  - needsMobile: grupla paylasilan kartlar mobil uygulamada yeniden sarilir.
 *  - lockedCards: acilamayan kendi kartlari bitirmeyi bekletiyor.
 *  - leaseHeld: baska bir cihaz tasimayi yurutuyor.
 *  - retryLater: gecici durum; sonraki giriste kaldigi yerden surer.
 *  - keyMissing: oturumdaki anahtar hesabin yayimlanmis anahtariyla eslesmiyor.
 */
export type RsaMigrationResult =
    | { kind: 'disabled' }
    | { kind: 'skipped' }
    | { kind: 'needPin' }
    | { kind: 'needPlace' }
    | { kind: 'needBackupSecret' }
    | { kind: 'wrongSecret' }
    | { kind: 'needFile'; blob: Uint8Array; purge: boolean }
    | { kind: 'fileMismatch'; blob: Uint8Array; purge: boolean }
    | { kind: 'done'; locked: number; fresh: boolean }
    | { kind: 'needsMobile'; reason: 'groups' }
    | { kind: 'lockedCards'; count: number }
    | { kind: 'leaseHeld' }
    | { kind: 'retryLater' }
    | { kind: 'keyMissing' };

const KIND: PendingKeyKind = 'rsamig';
const DEVICE_KEY = 'kx_mig_device';
const CLEAN_PREFIX = 'kx_mig_clean_';
const SETTLE_BATCH = 100;
const INBOX_PASSES = 6;

type Persist = 'saved' | 'failed' | 'accountChanged';
type SecureOutcome = null | RsaMigrationResult | 'dropBlocked';

/** Kosu boyunca tasinan baglam (tek hesap; uid her yazimdan once yeniden denetlenir). */
interface Ctx {
    uid: string;
    input: RsaMigrationInput;
    status: MigrationStatus;
}

/** Halka + yedek anahtari (BK); `rec` kalici kayit. */
interface Sealed {
    rec: PendingKeyRecord;
    ring: KeyRing;
}

class Stop {
    constructor(readonly result: RsaMigrationResult) {}
}

/**
 * 14f: panelde RSA -> PQ tasimasi (mobil rsa_migration_runner ile ayni API sozlesmesi).
 *
 * Sira: Status -> Lease -> halka (UMK dahil) muhurlenip kaydedilir -> yedek guvenceye alinir
 * (sunucu/dosya) -> Begin -> kendi kartlari Rotate -> gelen kutusu Settle -> Migrate (Finish) ->
 * eski anahtarlar yedekten, oturumdan ve tarayici depolarindan silinir (karar 1).
 *
 * Kesintiden devam: muhurlu halka + BK + adim ('sealed' | 'secured' | 'purgeSealed') + yeniden
 * sarilmis kart kimlikleri `rsamig:<uid>` kaydinda yuklemeden ONCE yazilir. Sunucu tarafindaki
 * adimlar (Begin, Rotate, Settle, Migrate) idempotenttir; asama her kosuda Status'tan okunur.
 * Hesap izolasyonu: her yazimdan once oturum uid'i karsilastirilir.
 */
@Injectable({ providedIn: 'root' })
export class RsaMigrationService {
    private auth = inject(AuthService);
    private crypto = inject(CryptoService);
    private backup = inject(KeyBackupService);
    private store = inject(PendingKeyStore);
    private rsaRemoval = inject(RsaRemovalStatusService);
    private cards = inject(CardApiService);
    private accountStatus = inject(AccountStatusService);

    /** Testte ana is parcacigi KDF'i ve hizli parametreler takilir. */
    kdf: Argon2Kdf = argon2Kdf;
    params = ARGON2_STANDARD;

    /** Giriste PIN'le acilan yedegin PIN'i (yalniz bellekte; karar 2: yeniden sorulmaz). */
    private login: { uid: string; pin: string; source: 'file' | 'server' } | null = null;
    private flight: { uid: string; promise: Promise<RsaMigrationResult> } | null = null;

    /** Giris ekrani: PIN'le acilan yedek (dosya ya da sunucu). Kalici yazilmaz. */
    rememberLogin(opts: { pin: string; source: 'file' | 'server' }): void {
        const uid = this.auth.getUserId();
        this.login = uid ? { uid, pin: opts.pin, source: opts.source } : null;
    }

    forgetLogin(): void {
        this.login = null;
    }

    /** Ayni hesabin ucustaki kosusu paylasilir (cift tetikleme iki muhur uretmez). */
    run(input: RsaMigrationInput = {}): Promise<RsaMigrationResult> {
        const uid = this.auth.getUserId();
        if (!uid) return Promise.resolve({ kind: 'skipped' });
        if (this.flight?.uid === uid) return this.flight.promise;
        const entry = { uid, promise: this.runInner(uid, { ...input }) };
        this.flight = entry;
        const clear = () => { if (this.flight === entry) this.flight = null; };
        entry.promise.then(clear, clear);
        return entry.promise;
    }

    private async runInner(uid: string, input: RsaMigrationInput): Promise<RsaMigrationResult> {
        try {
            const result = await this.flow(uid, input);
            if (result.kind === 'done') await this.refreshAccountStatus();
            return result;
        } catch (e) {
            if (e instanceof Stop) return e.result;
            console.warn('RsaMigrationFailed', e instanceof Error ? e.message : 'unknown');
            return { kind: 'retryLater' };
        }
    }

    private async flow(uid: string, input: RsaMigrationInput): Promise<RsaMigrationResult> {
        if (!(await this.flagOn())) return { kind: 'disabled' };
        const status = await this.readStatus();
        if (!status) return { kind: 'retryLater' };
        if (!status.enabled) return { kind: 'disabled' };
        const ctx: Ctx = { uid, input, status };
        if (status.stage >= 4) return this.afterDone(ctx);
        if (!this.auth.getPrivateKey() && !this.auth.getPqPrivateKey()) return { kind: 'skipped' };

        await this.lease();
        const sealed = await this.sealedRing(ctx);
        try {
            const sec = await this.secure(ctx, sealed, false);
            if (sec === 'dropBlocked') return this.drop(ctx, { kind: 'retryLater' });
            if (sec) return sec;
            const pub = this.publishedPq(ctx, sealed.ring);
            this.toSession(ctx, sealed.ring, pub);
            await this.begin(ctx, pub);
            const cards = await this.rotateCards(ctx, sealed);
            await this.settleInbox(ctx, sealed.ring);
            const st = await this.readStatus();
            if (!st) return { kind: 'retryLater' };
            ctx.status = st;
            if (st.stage === 3) {
                await this.finish(pub.pub as string);
            } else if (st.stage !== 4) {
                if (cards.groups > 0) return { kind: 'needsMobile', reason: 'groups' };
                if (cards.locked > 0) return { kind: 'lockedCards', count: cards.locked };
                console.info('RsaMigrationNotReady', st.stage, st.ownV1Cards, st.rsaRemaining, st.plainTags);
                return { kind: 'retryLater' };
            }
            const after = await this.readStatus();
            if (after) ctx.status = after;
            // ENT-4: asama Done -> yedek temizligi nasil biterse bitsin oturumdaki RSA dusurulur.
            this.dropSessionRsaAtDone(ctx.uid);
            const purged = await this.purge(ctx, sealed);
            if (purged) return purged;
            return this.cleanup(ctx, sealed.ring, true);
        } finally {
            wipeRing(sealed.ring);
            sealed.rec.bk?.fill(0);
        }
    }

    // ---------- halka ----------

    /** Kayit varsa onu acar (surdurme), yoksa halkayi kurar, muhurler ve yuklemeden once yazar. */
    private async sealedRing(ctx: Ctx): Promise<Sealed> {
        const rec = await this.store.load(ctx.uid, KIND);
        if (rec) {
            const ring = await this.openRecord(rec);
            const fp = ctx.status.pqFingerprint;
            if (ring && rec.step !== 'purgeSealed' && (!fp || ring.hasFp(fp))) return { rec, ring };
            console.info('RsaMigrationRecordStale; dropped', rec.step);
            if (ring) wipeRing(ring);
            rec.bk?.fill(0);
            await this.store.remove(ctx.uid, KIND);
        }
        return this.build(ctx);
    }

    private async build(ctx: Ctx): Promise<Sealed> {
        const onboarding = await this.onboarding();
        const server = await this.download();
        if (server === undefined) throw new Stop({ kind: 'retryLater' });
        const built = server && isV5(server)
            ? await this.fromServerV5(ctx, server, onboarding)
            : await this.fromSession(ctx, onboarding);
        const rec: PendingKeyRecord = {
            material: { privateKeyPem: '', publicKeyPem: '', pqPrivateKey: new Uint8Array(0), pqPublicKeyJson: '' },
            blob: built.blob,
            bk: built.bk,
            step: 'sealed',
            destination: built.place,
            kv: built.kv,
            serverPq: built.pub,
            createdAt: Date.now()
        };
        const saved = await this.persist(ctx.uid, rec);
        if (saved !== 'saved') {
            wipeRing(built.ring);
            built.bk.fill(0);
            throw new Stop(saved === 'accountChanged' ? { kind: 'skipped' } : { kind: 'retryLater' });
        }
        return { rec, ring: built.ring };
    }

    /**
     * Sunucuda v5 var: onun kilitleri (PIN ve kurtarma kodu) korunur, halka yalniz eksik anahtarlarla
     * genisler ve ayni BK ile yeniden muhurlenir. Acilamazsa oturum yoluna DUSULMEZ (kurtarma kodu
     * kaybolmasin diye); kullanicidan yedegin PIN'i ya da kurtarma kodu istenir.
     */
    private async fromServerV5(ctx: Ctx, bytes: Uint8Array, onboarding: { pub: string | null; kv: number | null | undefined }) {
        const parsed = parseV5(bytes);
        const opened = await this.openWithSecrets(ctx, parsed);
        let ring = opened.ring;
        try {
            ring = this.ensurePq(ring, onboarding.pub, onboarding.kv);
            ring = addSessionRsa(ring, this.auth.getPrivateKey());
            ring = this.ensureUmk(ring);
            const pub = this.pickPq(ring, ctx.status.pqFingerprint ?? null);
            const blob = encodeV5(await sealBody(ring, opened.bk), parsed.locks);
            return { ring, bk: opened.bk, blob, place: 'server' as RsaMigrationPlace, pub: pub.pub as string, kv: pub.kv };
        } catch (e) {
            wipeRing(ring);
            opened.bk.fill(0);
            throw e;
        }
    }

    /** Oturum yolu (karar 2): cihazdaki anahtarlar + tek yeni PIN; giriste kullanilan PIN yeniden sorulmaz. */
    private async fromSession(ctx: Ctx, onboarding: { pub: string | null; kv: number | null | undefined }) {
        const pin = this.loginPin(ctx.uid) ?? (ctx.input.pin && PIN_PATTERN.test(ctx.input.pin) ? ctx.input.pin : null);
        const place: RsaMigrationPlace | null = this.login?.uid === ctx.uid && this.login.source === 'server'
            ? 'server' : ctx.input.place ?? null;
        if (!pin) throw new Stop({ kind: 'needPin' });
        if (!place) throw new Stop({ kind: 'needPlace' });

        const pem = this.auth.getPrivateKey() ?? '';
        let ring: KeyRing | null;
        if (onboarding.pub) {
            ring = this.crypto.buildRing({ pem, pq: this.auth.getPqPrivateKey() }, onboarding.pub, onboarding.kv);
            if (!ring) {
                console.info('RsaMigrationKeyMissing: oturum PQ yayimlanan anahtari turetmiyor');
                throw new Stop({ kind: 'keyMissing' });
            }
        } else {
            const seed = this.auth.getPqPrivateKey() ?? generatePqKeyPair().privateKey;
            const pub = derivePqPublicKeyJson(seed);
            ring = this.crypto.buildRing({ pem, pq: seed }, pub, 1) as KeyRing;
        }
        try {
            ring = this.ensureUmk(ring);
        } catch (e) {
            wipeRing(ring);
            throw e;
        }
        const bk = this.crypto.newBackupKey();
        let blob: Uint8Array;
        try {
            blob = await sealV5(ring, bk, { pin }, defaultRng, this.params, this.kdf);
        } catch (e) {
            wipeRing(ring);
            bk.fill(0);
            throw e;
        }
        const pq = ring.current(RingAlg.pq) as RingKey;
        return { ring, bk, blob, place, pub: pq.pub as string, kv: pq.kv };
    }

    /** Sirlar sirasiyla: giris PIN'i, girilen PIN, girilen sir. PIN bicimindeki sir PIN kilidinde de denenir. */
    private async openWithSecrets(ctx: Ctx, file: BackupV5): Promise<{ ring: KeyRing; bk: Uint8Array }> {
        const secrets = [this.loginPin(ctx.uid), ctx.input.pin, ctx.input.secret]
            .filter((s, i, all): s is string => !!s && all.indexOf(s) === i);
        let tried = false;
        for (const secret of secrets) {
            const locks = file.locks.filter(l => l.type === 'rc' || (l.type === 'pin' && PIN_PATTERN.test(secret)));
            for (const lock of locks) {
                tried = true;
                const opened = await this.tryLock(lock, secret, file);
                if (opened) return opened;
            }
        }
        // Giris PIN'i tutmadiysa kullanicidan sir istenir; girilen sir tutmadiysa "yanlis".
        throw new Stop(tried && (ctx.input.pin || ctx.input.secret) ? { kind: 'wrongSecret' } : { kind: 'needBackupSecret' });
    }

    private async tryLock(lock: BackupLock, secret: string, file: BackupV5): Promise<{ ring: KeyRing; bk: Uint8Array } | null> {
        let bk: Uint8Array;
        try {
            bk = await openLock(lock, secret, this.kdf);
        } catch (e) {
            if (!(e instanceof KeyBackupWrongSecret)) console.warn('RsaMigrationLockUnreadable', e instanceof Error ? e.message : 'unknown');
            return null;
        }
        try {
            return { ring: await openBody(file, bk), bk };
        } catch (e) {
            console.warn('RsaMigrationBodyUnreadable', e instanceof Error ? e.message : 'unknown');
            bk.fill(0);
            return null;
        }
    }

    /** Yayimlanmis PQ halkada olmali; yoksa oturum PQ'su (yayimlananı turetiyorsa) eklenir. */
    private ensurePq(ring: KeyRing, pub: string | null, kv: number | null | undefined): KeyRing {
        if (pub) {
            if (ring.hasFp(pqFingerprint(pub))) return ring;
            const seed = this.auth.getPqPrivateKey();
            if (!seed || derivePqPublicKeyJson(seed) !== pub) {
                console.info('RsaMigrationKeyMissing: yayimlanan PQ halkada da oturumda da yok');
                throw new Stop({ kind: 'keyMissing' });
            }
            return addOrStop(ring, RingKey.pq({ kv: ringPqKeyVersion(kv), seed, pubJson: pub, fp: pqFingerprint(pub) }));
        }
        if (ring.current(RingAlg.pq)) return ring;
        const seed = this.auth.getPqPrivateKey() ?? generatePqKeyPair().privateKey;
        const pubJson = derivePqPublicKeyJson(seed);
        if (ring.hasFp(pqFingerprint(pubJson))) return ring;
        return addOrStop(ring, RingKey.pq({ kv: nextKv(ring, RingAlg.pq), seed, pubJson, fp: pqFingerprint(pubJson) }));
    }

    /** Halkada current UMK yoksa oturumdaki UMK, o da yoksa yeni rastgele UMK eklenir. */
    private ensureUmk(ring: KeyRing): KeyRing {
        const session = this.auth.getUmk();
        try {
            if (session && !ring.hasFp(umkFingerprint(session.key)) && !ring.byVersion(RingAlg.umk, session.kv)) {
                return addOrStop(ring, RingKey.umk({ kv: session.kv, key: session.key, fp: umkFingerprint(session.key) }));
            }
            if (ring.current(RingAlg.umk)) return ring;
            const key = randomBytes(32);
            try {
                return addOrStop(ring, RingKey.umk({ kv: nextKv(ring, RingAlg.umk), key, fp: umkFingerprint(key) }));
            } finally {
                key.fill(0);
            }
        } finally {
            session?.key.fill(0);
        }
    }

    private pickPq(ring: KeyRing, fp: string | null): RingKey {
        const chosen = (fp ? ring.keys.find(k => k.alg === RingAlg.pq && k.fp === fp) : undefined) ?? ring.current(RingAlg.pq);
        if (!chosen?.pub) throw new Stop({ kind: 'keyMissing' });
        return chosen;
    }

    private publishedPq(ctx: Ctx, ring: KeyRing): RingKey {
        return this.pickPq(ring, ctx.status.pqFingerprint ?? null);
    }

    // ---------- yedegin guvenceye alinmasi ----------

    /** Sunucu ve/veya dosya kopyasi dogrulanir; her alt adim kayda islenir. */
    private async secure(ctx: Ctx, s: Sealed, purge: boolean): Promise<SecureOutcome> {
        const rec = s.rec;
        const place = rec.destination as RsaMigrationPlace;
        if ((place === 'server' || place === 'both') && !rec.serverDone) {
            const r = await this.secureServer(ctx, rec, purge);
            if (r) return r;
        }
        if ((place === 'download' || place === 'both') && !rec.fileDone) {
            const r = await this.secureFile(ctx, rec, purge);
            if (r) return r;
        }
        if (rec.step === 'sealed') {
            rec.step = 'secured';
            const saved = await this.persist(ctx.uid, rec);
            if (saved === 'accountChanged') return { kind: 'skipped' };
        }
        return null;
    }

    private async secureServer(ctx: Ctx, rec: PendingKeyRecord, purge: boolean): Promise<SecureOutcome> {
        const blob = rec.blob as Uint8Array;
        const before = await this.download();
        if (before === undefined) return { kind: 'retryLater' };
        if (!before || !(await this.isOurs(before, rec))) {
            if (!this.sameAccount(ctx.uid)) return { kind: 'skipped' };
            const up = await firstValueFrom(this.backup.uploadChecked(blob));
            if (!up.ok) return this.backupRejected(ctx, 'upload', up.status, up.code, purge);
            const back = await this.download();
            if (!back || !(await this.isOurs(back, rec))) {
                console.warn('RsaMigrationVerifyMismatch');
                return { kind: 'retryLater' };
            }
        }
        rec.serverDone = true;
        return (await this.persist(ctx.uid, rec)) === 'accountChanged' ? { kind: 'skipped' } : null;
    }

    private async secureFile(ctx: Ctx, rec: PendingKeyRecord, purge: boolean): Promise<SecureOutcome> {
        const blob = rec.blob as Uint8Array;
        const file = ctx.input.file;
        // Dosya bir kez kullanilir: sonraki adim (ornegin temizlik) yeni dosya ister.
        ctx.input.file = undefined;
        if (!file) return { kind: 'needFile', blob: new Uint8Array(blob), purge };
        if (!(await this.isOurs(file, rec))) {
            console.info('RsaMigrationFileNotMatching');
            return { kind: 'fileMismatch', blob: new Uint8Array(blob), purge };
        }
        if (!this.sameAccount(ctx.uid)) return { kind: 'skipped' };
        const proof = await firstValueFrom(this.backup.fileProof(parseV5(blob).ring));
        if (!proof.ok) return this.backupRejected(ctx, 'proof', proof.status, proof.code, purge);
        rec.fileDone = true;
        return (await this.persist(ctx.uid, rec)) === 'accountChanged' ? { kind: 'skipped' } : null;
    }

    private async backupRejected(ctx: Ctx, what: string, status: number, code: string | null, purge: boolean): Promise<SecureOutcome> {
        console.warn('RsaMigrationBackupRejected', what, status, code);
        if (code === 'KeyBackupDropBlocked') {
            if (purge) return 'dropBlocked';
            // Sunucu halkasinda bizde olmayan anahtar var (baska cihaz): kayit atilir, sonraki kosu sunucu v5'ini acar.
            return this.drop(ctx, { kind: 'retryLater' });
        }
        if (code === 'KeyBackupFingerprintMissing') return this.drop(ctx, { kind: 'keyMissing' });
        if (code === 'InvalidEnvelope') return this.drop(ctx, { kind: 'retryLater' });
        return { kind: 'retryLater' };
    }

    private async drop(ctx: Ctx, result: RsaMigrationResult): Promise<RsaMigrationResult> {
        if (this.sameAccount(ctx.uid)) await this.store.remove(ctx.uid, KIND);
        return result;
    }

    /** Baytlar kayitli blobla ayni ya da kayitli BK ile ayni halkayi aciyor mu. */
    private async isOurs(bytes: Uint8Array, rec: PendingKeyRecord): Promise<boolean> {
        const blob = rec.blob as Uint8Array;
        if (bytesEqual(bytes, blob)) return true;
        let a: KeyRing | null = null;
        let b: KeyRing | null = null;
        try {
            a = await openBody(parseV5(blob), rec.bk as Uint8Array);
            b = await openBody(parseV5(bytes), rec.bk as Uint8Array);
            return a.sameAs(b);
        } catch (e) {
            // Acilamayan kopya "bizim degil" sayilir; neden loglanir (anahtar tasimaz).
            console.info('RsaMigrationCopyNotOurs', e instanceof Error ? e.message : 'unknown');
            return false;
        } finally {
            if (a) wipeRing(a);
            if (b) wipeRing(b);
        }
    }

    // ---------- oturum ve Begin ----------

    /** Oturuma yayimlanan PQ ve current UMK yazilir (kart/gelen kutusu yeni zarflari bununla yazar). */
    private toSession(ctx: Ctx, ring: KeyRing, pq: RingKey): void {
        if (!this.sameAccount(ctx.uid)) throw new Stop({ kind: 'skipped' });
        const sessionPq = this.auth.getPqPrivateKey();
        const pqOk = !!sessionPq && safeDerive(sessionPq) === pq.pub;
        const rsa = this.auth.getPrivateKey() || ring.rsaPems[0] || '';
        if (!pqOk || (!this.auth.hasRsaKey() && rsa)) {
            const seed = pq.seed as Uint8Array;
            this.auth.storePrivateKey(rsa, seed, pq.kv);
            seed.fill(0);
        }
        const umk = ring.current(RingAlg.umk) as RingKey;
        const key = umk.key as Uint8Array;
        this.auth.storeUmk(umk.kv, key);
        key.fill(0);
    }

    /** Yayimlanmis ve kv'si atanmis PQ ise atlanir; 121 donemi (kv yok) anahtar da Begin kapisindan gecer. */
    private async begin(ctx: Ctx, pq: RingKey): Promise<void> {
        if (published(ctx.status, pq)) return;
        try {
            await firstValueFrom(this.cards.beginMigration(pq.pub as string));
        } catch (e) {
            const { code } = httpError(e);
            const st = await this.readStatus();
            if (st && published(st, pq)) {
                ctx.status = st;
                return;
            }
            console.warn('RsaMigrationBeginFailed', code);
            if (code === 'PqKeyMismatch') throw new Stop(await this.drop(ctx, { kind: 'retryLater' }));
            if (code === 'MigrationLeaseHeld') throw new Stop({ kind: 'leaseHeld' });
            throw new Stop({ kind: 'retryLater' });
        }
        const st = await this.readStatus();
        if (st) ctx.status = st;
    }

    private async lease(): Promise<void> {
        try {
            await firstValueFrom(this.cards.migrationLease(deviceId()));
        } catch (e) {
            const { code } = httpError(e);
            if (code === 'MigrationLeaseHeld') throw new Stop({ kind: 'leaseHeld' });
            // LeaseBusy ve gecici hatalar: kosu surer, korunan uclar gerekirse kendisi reddeder.
            console.info('RsaMigrationLeaseNotTaken', code);
        }
    }

    // ---------- kendi kartlari ----------

    private async rotateCards(ctx: Ctx, s: Sealed): Promise<{ groups: number; locked: number }> {
        const out = { groups: 0, locked: 0 };
        if (ctx.status.ownV1Cards <= 0) return out;
        await this.lease();
        let items: CardDeltaItem[];
        try {
            items = await firstValueFrom(this.cards.fetchAllDelta());
        } catch (e) {
            console.warn('RsaMigrationDeltaFailed', httpError(e).status);
            throw new Stop({ kind: 'retryLater' });
        }
        const done = new Set(s.rec.rotated ?? []);
        for (const item of items) {
            if (item.role !== 'owner' || !item.active || done.has(item.cardId)) continue;
            // Kisisel sahip zarfi olan kart zaten yeniden sarilmis (Rotate ya da bayrak acikken yazilmis).
            if (item.visibility !== 0 && item.ownKey) continue;
            const r = await this.rotateOne(item);
            if (r === 'groups') out.groups++;
            else if (r === 'locked') out.locked++;
            else {
                done.add(item.cardId);
                s.rec.rotated = [...done];
                if ((await this.persist(ctx.uid, s.rec)) === 'accountChanged') throw new Stop({ kind: 'skipped' });
            }
        }
        return out;
    }

    private async rotateOne(item: CardDeltaItem): Promise<'ok' | 'groups' | 'locked'> {
        const card = await this.cards.cardFromDelta(item, async () => null);
        if (card.locked) {
            console.info('RsaMigrationCardLocked', item.cardId);
            return 'locked';
        }
        // ENT-3: anahtarsiz sifreli kart yalniz v1 govdedir (legacyCardData); Rotate ona yeni anahtar uretir.
        // v2 zarfli kart anahtarsiz gelirse yeni anahtarla yazilmaz (fail-closed): kilitli sayilir, Finish bekler.
        if (item.visibility !== 0 && !card.cardKeyB64 && !item.legacyCardData) {
            console.warn('RsaMigrationCardKeyMissing', item.cardId);
            return 'locked';
        }
        let req: RotateCardRequest;
        let keyB64: string | null = null;
        if (item.visibility === 0) {
            const b = await this.cards.buildUpsertV2(item.cardId, 0, card.v2Fields ?? []);
            req = {
                iv: null, payload: null, plainData: b.req.plainData, bodyVersion: Math.max(1, item.bodyVersion + 1),
                ownKey: null, objectWraps: [], recipients: [], groups: []
            };
        } else {
            const b = await this.cards.buildUpsertV2(item.cardId, item.visibility, card.v2Fields ?? [],
                { cardKeyB64: card.cardKeyB64 ?? null, bodyVersion: item.bodyVersion });
            keyB64 = b.cardKeyB64;
            req = {
                iv: b.req.iv, payload: b.req.payload, plainData: null, bodyVersion: b.req.bodyVersion,
                ownKey: b.req.ownKey ?? null, objectWraps: [], recipients: [], groups: []
            };
        }
        for (let attempt = 0; attempt < 2; attempt++) {
            const shares = await this.shares(item.cardId);
            if (shares === 'gone') return 'ok';
            const hasGroups = (shares.groups ?? []).some(g => g.active);
            if (keyB64) {
                const recipients = await this.recipients(shares, keyB64);
                if (!recipients) return 'groups';
                req.recipients = recipients;
            }
            try {
                await firstValueFrom(this.cards.rotateCard(item.cardId, req));
                return 'ok';
            } catch (e) {
                const { code } = httpError(e);
                if (code === 'KeyChanged' || code === 'CardNotFound') return 'ok';
                if (code === 'MigrationLeaseHeld') throw new Stop({ kind: 'leaseHeld' });
                if (code === 'RotateRecipientsStale' || code === 'GroupKeyVersionStale') {
                    // Panel grup zarfi yazamaz: uye olunan grupla paylasilan kart mobilde yeniden sarilir.
                    if (hasGroups) {
                        console.info('RsaMigrationCardNeedsMobile', item.cardId);
                        return 'groups';
                    }
                    continue;
                }
                console.warn('RsaMigrationRotateFailed', item.cardId, code);
                throw new Stop({ kind: 'retryLater' });
            }
        }
        console.warn('RsaMigrationRotateStillStale', item.cardId);
        throw new Stop({ kind: 'retryLater' });
    }

    private async shares(cardId: string): Promise<CardSharesResponse | 'gone'> {
        try {
            return await firstValueFrom(this.cards.getCardShares(cardId));
        } catch (e) {
            const { code, status } = httpError(e);
            if (code === 'CardNotFound' || status === 404) return 'gone';
            console.warn('RsaMigrationSharesFailed', status, code);
            throw new Stop({ kind: 'retryLater' });
        }
    }

    /** Her aktif paylasima yeni anahtar: tasinmis aliciya PQ, tasinmamisa RSA. Ikisi de yoksa null. */
    private async recipients(shares: CardSharesResponse, keyB64: string): Promise<RotateRecipient[] | null> {
        const out: RotateRecipient[] = [];
        for (const s of shares.items.filter(x => x.active)) {
            if (s.pqPublicKey) {
                out.push({ sharedCardId: s.shareId, pqSalt: await this.cards.wrapForRecipient(s.pqPublicKey, keyB64, s.pqKeyVersion) });
            } else if (s.rsaPublicKey) {
                out.push({ sharedCardId: s.shareId, rsaSalt: rsaWrapKey(s.rsaPublicKey, unb64(keyB64)) });
            } else {
                console.warn('RsaMigrationRecipientKeyMissing', s.shareId);
                return null;
            }
        }
        return out;
    }

    // ---------- gelen kutusu ----------

    private async settleInbox(ctx: Ctx, ring: KeyRing): Promise<void> {
        if (ctx.status.rsaRemaining + ctx.status.plainTags + ctx.status.pending <= 0) return;
        await this.lease();
        const opener = new InboxOpener(ring);
        const umk = ring.current(RingAlg.umk) as RingKey;
        // Sunucunun bicim/surum nedeniyle reddettigi oge sonraki geciste kilitli yerlesir (takili kalmaz).
        const forceLocked = new Set<string>();
        for (let pass = 0; pass < INBOX_PASSES; pass++) {
            const items = await this.inboxAll();
            if (items.length === 0) return;
            const settle: SettleItem[] = [];
            for (const it of items) {
                const stamp = it.type === 'SharedCard' ? { keyStamp: it.keyStamp ?? null } : {};
                if (forceLocked.has(it.type + ':' + it.id)) {
                    settle.push({ type: it.type, id: it.id, locked: true, ...stamp });
                    continue;
                }
                const o = await opener.open(it);
                if (o.kind === 'plain') {
                    const key = umk.key as Uint8Array;
                    try {
                        settle.push({ type: it.type, id: it.id, own: await sealPersonal(key, umk.kv, o.ad, o.bytes), ...stamp });
                    } finally {
                        key.fill(0);
                        o.bytes.fill(0);
                    }
                } else if (o.kind === 'text') {
                    settle.push({ type: it.type, id: it.id, plain: o.text });
                } else if (o.kind === 'locked') {
                    settle.push({ type: it.type, id: it.id, locked: true, ...stamp });
                }
            }
            let progress = 0;
            for (let i = 0; i < settle.length; i += SETTLE_BATCH) {
                if (!this.sameAccount(ctx.uid)) throw new Stop({ kind: 'skipped' });
                let results;
                try {
                    results = (await firstValueFrom(this.cards.settleInbox(settle.slice(i, i + SETTLE_BATCH)))).results;
                } catch (e) {
                    const { code, status } = httpError(e);
                    if (code === 'MigrationLeaseHeld') throw new Stop({ kind: 'leaseHeld' });
                    console.warn('RsaMigrationSettleFailed', status, code);
                    throw new Stop({ kind: 'retryLater' });
                }
                for (const r of results) {
                    if (r.outcome === 'Settled' || r.outcome === 'Locked' || r.outcome === 'NotFound') progress++;
                    else if (r.outcome !== 'KeyChanged' && r.outcome !== 'Retry') {
                        console.info('RsaMigrationSettleRejected', r.type, r.outcome);
                        forceLocked.add(r.type + ':' + r.id);
                        progress++;
                    }
                }
            }
            if (progress === 0) return;
        }
    }

    private async inboxAll(): Promise<InboxItem[]> {
        const all: InboxItem[] = [];
        let cursor: string | null = null;
        try {
            do {
                const page: InboxPage = await firstValueFrom(this.cards.inboxPage(cursor, false));
                all.push(...page.items);
                cursor = page.cursor ?? null;
            } while (cursor);
        } catch (e) {
            console.warn('RsaMigrationInboxFailed', httpError(e).status);
            throw new Stop({ kind: 'retryLater' });
        }
        return all;
    }

    // ---------- bitis ve temizlik ----------

    private async finish(pub: string): Promise<void> {
        try {
            await firstValueFrom(this.cards.migrateAccount(pub));
        } catch (e) {
            const { code, status } = httpError(e);
            const st = await this.readStatus();
            if (st?.stage === 4) return;
            console.warn('RsaMigrationFinishFailed', status, code);
            throw new Stop({ kind: 'retryLater' });
        }
    }

    /**
     * Karar 1: tasima bitince yedekteki RSA ve tum eski anahtarlar dusurulur (ayni BK ve kilitler,
     * yeniden muhur). Sunucu dusurmeyi reddederse once yalniz RSA dusurulur; o da olmazsa loglanir.
     */
    private async purge(ctx: Ctx, s: Sealed): Promise<RsaMigrationResult | null> {
        if (!s.ring.hasRetired && s.rec.step !== 'purgeSealed') return null;
        const fp = ctx.status.pqFingerprint ?? null;
        if (s.rec.step !== 'purgeSealed') {
            const r = await this.reseal(ctx, s, keepPublished(s.ring.onlyCurrent(), s.ring, fp));
            if (r) return r;
        }
        const sec = await this.secure(ctx, s, true);
        if (sec === 'dropBlocked') {
            const loose = s.ring.withoutRsa();
            if (!loose.sameAs(keepPublished(s.ring.onlyCurrent(), s.ring, fp))) {
                const r = await this.reseal(ctx, s, loose);
                if (r) return r;
                const again = await this.secure(ctx, s, true);
                if (again && again !== 'dropBlocked') return again;
                if (!again) return null;
            }
            console.warn('RsaMigrationPurgeDropBlocked; eski anahtarlar sunucu halkasinda kaldi');
            return null;
        }
        return sec;
    }

    private async reseal(ctx: Ctx, s: Sealed, target: KeyRing): Promise<RsaMigrationResult | null> {
        const locks = parseV5(s.rec.blob as Uint8Array).locks;
        s.rec.blob = encodeV5(await sealBody(target, s.rec.bk as Uint8Array), locks);
        s.rec.step = 'purgeSealed';
        s.rec.serverDone = false;
        s.rec.fileDone = false;
        const saved = await this.persist(ctx.uid, s.rec);
        if (saved === 'accountChanged') return { kind: 'skipped' };
        if (saved === 'failed') return { kind: 'retryLater' };
        return null;
    }

    /** Asama Done: yarim kalan temizlik surer; yoksa sunucu yedeginde eski anahtar var mi bakilir. */
    private async afterDone(ctx: Ctx): Promise<RsaMigrationResult> {
        // ENT-4: Done'da sunucuda RSA zarfi kalmadi (iki asama: V2 ayari ve son form kontrolu Finish'ten
        // once bitti). Oturumdaki RSA artik gereksiz; yedek temizligi girdi isterse ya da yarida
        // kalirsa (retryLater/needBackupSecret...) da oturumda/sessionStorage'da birakilmaz.
        this.dropSessionRsaAtDone(ctx.uid);
        if (this.cleanMarked(ctx.uid)) {
            this.forgetLogin();
            return { kind: 'done', locked: ctx.status.locked, fresh: false };
        }
        let sealed: Sealed | null = null;
        const rec = await this.store.load(ctx.uid, KIND);
        if (rec) {
            const ring = rec.step === 'secured' || rec.step === 'purgeSealed' ? await this.openRecord(rec) : null;
            if (ring) sealed = { rec, ring };
            else await this.store.remove(ctx.uid, KIND);
        }
        if (!sealed) {
            const server = await this.download();
            if (server === undefined) return { kind: 'retryLater' };
            if (server && isV5(server) && headerHasRetired(parseV5(server).ring)) {
                const opened = await this.openWithSecrets(ctx, parseV5(server));
                sealed = {
                    ring: opened.ring,
                    rec: {
                        material: { privateKeyPem: '', publicKeyPem: '', pqPrivateKey: new Uint8Array(0), pqPublicKeyJson: '' },
                        blob: server, bk: opened.bk, step: 'secured', destination: 'server', createdAt: Date.now()
                    }
                };
            }
        }
        if (!sealed) return this.cleanup(ctx, null, false);
        try {
            const purged = await this.purge(ctx, sealed);
            if (purged) return purged;
            return this.cleanup(ctx, sealed.ring, false);
        } finally {
            wipeRing(sealed.ring);
            sealed.rec.bk?.fill(0);
        }
    }

    /** Oturumdan ve tarayici depolarindan eski anahtarlar silinir; halka boyutu sayaci loglanir. */
    private async cleanup(ctx: Ctx, ring: KeyRing | null, fresh: boolean): Promise<RsaMigrationResult> {
        if (!this.sameAccount(ctx.uid)) return { kind: 'skipped' };
        this.auth.dropRetiredSessionKeys();
        for (const kind of ['rsamig', 'rotation', 'v5convert', 'onboard'] as PendingKeyKind[]) {
            try {
                await this.store.remove(ctx.uid, kind);
            } catch (e) {
                console.warn('RsaMigrationCleanupRemoveFailed', kind, e instanceof Error ? e.message : 'unknown');
            }
        }
        this.forgetLogin();
        // Yalniz sayilar (alg + durum); anahtar ya da parmak izi loglanmaz.
        if (ring) console.info('KeyRingSize', ring.onlyCurrent().countCode, 'before', ring.countCode);
        this.markClean(ctx.uid);
        return { kind: 'done', locked: ctx.status.locked, fresh };
    }

    // ---------- yardimcilar ----------

    private async openRecord(rec: PendingKeyRecord): Promise<KeyRing | null> {
        if (!rec.blob || !rec.bk || !rec.destination || !rec.step) return null;
        try {
            return await openBody(parseV5(rec.blob), rec.bk);
        } catch (e) {
            console.warn('RsaMigrationRecordUnreadable', e instanceof Error ? e.message : 'unknown');
            return null;
        }
    }

    private loginPin(uid: string): string | null {
        return this.login?.uid === uid ? this.login.pin : null;
    }

    private async persist(uid: string, rec: PendingKeyRecord): Promise<Persist> {
        try {
            return await saveForAccount(this.store, uid, KIND, rec, () => this.auth.getUserId());
        } catch (e) {
            console.warn('RsaMigrationRecordNotSaved', rec.step, e instanceof Error ? e.message : 'unknown');
            return 'failed';
        }
    }

    /** undefined = okunamadi (loglandi), null = sunucuda yedek yok. */
    private async download(): Promise<Uint8Array | null | undefined> {
        try {
            const buf = await firstValueFrom(this.backup.download());
            return buf ? new Uint8Array(buf) : null;
        } catch (e) {
            console.warn('RsaMigrationDownloadFailed', httpError(e).status);
            return undefined;
        }
    }

    /**
     * Tasima hesabi v2'ye almis olabilir; onbellekteki v1 durumu Kartlar seridini ve migrate-v2
     * sayfasini "Tasimayi baslat" gostermeye surukler (E2E bulgu 24 eki). Hata sonucu degistirmez.
     */
    private async refreshAccountStatus(): Promise<void> {
        this.accountStatus.reset();
        try {
            await firstValueFrom(this.accountStatus.load(true));
        } catch (e) {
            console.warn('RsaMigrationAccountStatusRefreshFailed', httpError(e).status);
        }
    }

    private async readStatus(): Promise<MigrationStatus | null> {
        try {
            return await firstValueFrom(this.cards.migrationStatus());
        } catch (e) {
            console.warn('RsaMigrationStatusUnavailable', httpError(e).status);
            return null;
        }
    }

    private async onboarding(): Promise<{ pub: string | null; kv: number | null | undefined }> {
        try {
            const s = await firstValueFrom(this.auth.getOnboardingStatus());
            return { pub: s.pqPublicKey ?? null, kv: s.pqKeyVersion };
        } catch (e) {
            console.warn('RsaMigrationOnboardingUnavailable', httpError(e).status);
            throw new Stop({ kind: 'retryLater' });
        }
    }

    private async flagOn(): Promise<boolean> {
        try {
            return await firstValueFrom(this.rsaRemoval.load());
        } catch (e) {
            console.warn('RsaMigrationFlagUnknown', e instanceof Error ? e.message : 'unknown');
            return false;
        }
    }

    private sameAccount(uid: string): boolean {
        return this.auth.getUserId() === uid;
    }

    /** Asama Done: eski oturum anahtarlari (RSA + onceki PQ) bellekten ve sessionStorage'dan silinir. */
    private dropSessionRsaAtDone(uid: string): void {
        if (!this.sameAccount(uid)) return;
        this.auth.dropRetiredSessionKeys();
    }

    private cleanMarked(uid: string): boolean {
        try {
            return localStorage.getItem(CLEAN_PREFIX + uid) === '1';
        } catch {
            return false;
        }
    }

    private markClean(uid: string): void {
        try {
            localStorage.setItem(CLEAN_PREFIX + uid, '1');
        } catch (e) {
            // Isaret yazilamazsa sonraki giris temizligi yeniden dener (zararsiz).
            console.info('RsaMigrationMarkerNotSaved', e instanceof Error ? e.message : 'unknown');
        }
    }
}

function published(st: MigrationStatus, pq: RingKey): boolean {
    return st.pqFingerprint === pq.fp && st.pqKeyVersion != null;
}

function httpError(e: unknown): { status: number; code: string | null } {
    if (e instanceof HttpErrorResponse) return { status: e.status, code: readCode(e.error) };
    return { status: 0, code: null };
}

/** Tarayici basina sabit, rastgele cihaz kimligi (kira icin); kisisel veri tasimaz. */
function deviceId(): string {
    try {
        const existing = localStorage.getItem(DEVICE_KEY);
        if (existing) return existing;
        const id = b64url(randomBytes(16));
        localStorage.setItem(DEVICE_KEY, id);
        return id;
    } catch {
        return b64url(randomBytes(16));
    }
}

function b64url(bytes: Uint8Array): string {
    let bin = '';
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function addOrStop(ring: KeyRing, key: RingKey): KeyRing {
    try {
        return ring.add(key);
    } catch (e) {
        if (e instanceof KeyRingConflict) {
            console.warn('RsaMigrationRingConflict', e.alg, e.kv);
            throw new Stop({ kind: 'keyMissing' });
        }
        throw e;
    }
}

function addSessionRsa(ring: KeyRing, pem: string | null): KeyRing {
    if (!pem?.trim()) return ring;
    try {
        return ring.add(RingKey.rsa({ pem: pem.trim(), fp: rsaFingerprint(pem.trim()) }));
    } catch (e) {
        console.warn('RsaMigrationSessionRsaSkipped', e instanceof Error ? e.message : 'unknown');
        return ring;
    }
}

function nextKv(ring: KeyRing, alg: string): number {
    return ring.keys.filter(k => k.alg === alg).reduce((m, k) => Math.max(m, k.kv), 0) + 1;
}

/** Yayimlanan PQ current degilse de dusurulmez (sunucu yayimlanan anahtarin halkada kalmasini ister). */
function keepPublished(target: KeyRing, full: KeyRing, fp: string | null): KeyRing {
    if (!fp || target.hasFp(fp)) return target;
    const published = full.keys.find(k => k.fp === fp);
    if (!published) return target;
    try {
        return target.add(published.withState('old'));
    } catch {
        return target;
    }
}

/** Baslikta RSA ya da birden fazla PQ/UMK var mi (anahtar acmadan; Done sonrasi temizlik karari). */
function headerHasRetired(header: readonly { alg: string }[]): boolean {
    const count = (alg: string) => header.filter(e => e.alg === alg).length;
    return count(RingAlg.rsa) > 0 || count(RingAlg.pq) > 1 || count(RingAlg.umk) > 1;
}

function safeDerive(seed: Uint8Array): string | null {
    try {
        return derivePqPublicKeyJson(seed);
    } catch {
        return null;
    }
}

function wipeRing(ring: KeyRing): void {
    for (const key of ring.keys) key.wipe();
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}
